/* eslint-disable react/require-default-props */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect } from 'react';
import { Controller, useFormState } from 'react-hook-form';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Dropdown from '../Dropdown';
import InputField from '../../../../elements/InputField';
import SwitchComponent from '../SwitchComponent';
import ToolTip from '../../../../elements/ToolTip';
import products from '../products';
import templateFieldNames from './templateFieldNames';

function Step4Payouts({ index, updateWorkflowStatus, form, signalNext, setSignalNext }) {
  const { t: tL } = useTranslation('app', { keyPrefix: 'schema.pension.account' });
  const { t: tI } = useTranslation('addAccount', { keyPrefix: 'pension' });

  // ---------------------------------
  // process input data
  // ---------------------------------

  const { register, watch, getValues, control, setValue, trigger } = form;
  const { errors, isValid } = useFormState({
    control,
    name: ['payoutsPhaseStart', 'payoutsFrequency', 'payoutsDuration', 'payoutsDurationUserInput', 'payoutDurationFrequencyUnitUserInput', 'payoutsTaxable', 'payoutsIndexed'],
  });
  const durationFrequencyUnit = watch('payoutDurationFrequencyUnitUserInput');
  const durationInput = watch('payoutsDurationUserInput');
  const payoutsIndexed = watch('payoutsIndexed');

  // ---------------------------------
  // internal form logic
  // ---------------------------------

  // initialise
  useEffect(() => {
    const payoutDuration = getValues('payoutDuration'); // from formState

    // set human-readable unit in frequency dialog
    switch (payoutDuration) {
      case 3:
        setValue('payoutDurationFrequencyUnitUserInput', 'months', { shouldDirty: false });
        break;
      case 12:
        setValue('payoutDurationFrequencyUnitUserInput', 'years', { shouldDirty: false });
        break;
      case 0:
      default:
        setValue('payoutDurationFrequencyUnitUserInput', 'indefinite', { shouldDirty: false });
    }

    if (payoutDuration === undefined || payoutDuration === 0) {
      setValue('payoutDurationFrequencyUnitUserInput', 'indefinite', { shouldDirty: false });
    }
  }, []);

  // convert form user inputs to form state values
  useEffect(() => {
    // payoutsDurationUserInput + Frequency --> payoutsDuration
    const payoutDurationFrequencyUnitUserInput = getValues('payoutDurationFrequencyUnitUserInput');
    const payoutDurationUserInput = getValues('payoutsDurationUserInput');

    if (payoutDurationFrequencyUnitUserInput === 'indefinite') {
      setValue('payoutsDuration', 0);
    } else if (payoutDurationUserInput) {
      const numeric = Number(payoutDurationUserInput);
      if (!Number.isNaN(numeric)) {
        const months = durationFrequencyUnit === 'years' ? numeric * 12 : numeric;
        setValue('payoutsDuration', months);
      }
    } else {
      setValue('payoutsDuration', null);
    }
  }, [durationFrequencyUnit, durationInput]);

  // -----------------------------------------------
  // synchronise this form with parent form's state
  // -----------------------------------------------

  useEffect(() => {
    updateWorkflowStatus(index, isValid);
  }, [isValid]);

  // ---------------------------------
  // receive signal next from nav component
  // ---------------------------------

  useEffect(() => {
    if (signalNext) {
      const fieldsToValidate = ['payoutsPhaseStart', 'payoutsFrequency'];
      trigger(fieldsToValidate);
      setSignalNext(false);
    }
  }, [signalNext, trigger, setSignalNext, payoutsIndexed]);

  //---------------------------------------------------------------------------------------------
  // set product to "custom" if the user changes any of the connected fields
  //---------------------------------------------------------------------------------------------

  // icon and id are in products, need to add the translations here
  const productList = products.pension.map((p) => ({
    ...p,
    name: tI(`product.${p.id}.name`),
    description: tI(`product.${p.id}.description`),
  }));

  const product = watch('product'); // this field's value is the product ID

  // watch all fields determined by a pension template array members
  const templateFieldsValues = watch(templateFieldNames); // this destructures to an array, i.e. const [value1, value2] = watch(['field1', 'field2'])

  // if the product is not on "custom" and the user changes any connected form fields, "product" should be reset to "custom"
  useEffect(() => {
    if (product && product !== 'custom') {
      // if a defined product is selected, check if all product fields in the state are equal to the template fields
      const selectedProduct = productList.find((p) => p.id === product);
      // console.log('handler for >custom< is running for product', product, 'selectedProduct', selectedProduct, 'templateFieldsValues', templateFieldsValues, 'getValues', getValues());
      const allFieldsEqual = templateFieldsValues.every((value, idx) => {
        const fieldName = templateFieldNames[idx];
        // console.log('comparing form state field', fieldName, value, 'with product template field value', selectedProduct[fieldName]);
        return value === selectedProduct[fieldName];
      });
      if (!allFieldsEqual) setValue('product', 'custom');
    }
  }, [templateFieldsValues]);

  return (
    <div className="pt-4 sm:pt-0 grid sm:grid-cols-2 gap-4 sm:gap-x-8">
      <div className="sm:col-span-2">
        <h3 className="text-xl md:text-2xl font-bold leading-6 text-gray-900">{tI('wizardSteps.payouts.title')}</h3>
        <p className="mt-1 pb-4 text-sm text-gray-500">{tI('wizardSteps.payouts.subtitle')}</p>
      </div>

      <InputField
        id="payoutsPhaseStart"
        category="pension"
        type="date"
        optional={false}
        autoComplete="off"
        register={register}
        errors={errors}
        validationRules={{
          required: { value: true, message: tL('payoutsPhaseStart.error') },
          // setValueAs: (value) => (value ? dayjs.utc(value).valueOf() : value),
        }}
      />
      <Dropdown
        id="payoutsFrequency"
        list={[
          null,
          { id: 1, name: tL('parameters.payoutsFrequency.1') },
          { id: 3, name: tL('parameters.payoutsFrequency.3') },
          { id: 12, name: tL('parameters.payoutsFrequency.12') },
          { id: 0, name: tL('parameters.payoutsFrequency.0') },
        ]}
        label={tL('payoutsFrequency.label')}
        placeholder={tL('payoutsFrequency.placeholder')}
        toolTip={tL('payoutsFrequency.tooltip')}
        register={register}
        errors={errors}
        control={control}
        optional={false}
        validationRules={{ required: { value: true, message: tL('payoutsFrequency.error') } }}
      />

      <div>
        <div className="flex justify-between">
          <label htmlFor="payoutsDurationUserInput" className="text-sm font-medium text-gray-700 flex justify-start">
            {tL('payoutsDuration.label')}
            <ToolTip info={tL('payoutsDurationUserInput.tooltip')} />
          </label>
        </div>
        <div className="mt-1 relative rounded-md shadow-sm">
          <div className="grid grid-cols-2 gap-4">
            <input
              id="payoutsDurationUserInput"
              type="number"
              autoComplete="off"
              disabled={durationFrequencyUnit === 'indefinite'}
              className={`block w-full focus:outline-none sm:text-sm rounded-md ${
                errors?.payoutsDuration
                  ? 'border-red-300 text-red-900  placeholder-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 placeholder-gray-400 focus:ring-brandBlue-500 focus:border-brandBlue-500'
              }`}
              {...register('payoutsDurationUserInput')}
            />
            <select
              id="payoutDurationFrequencyUnitUserInput"
              className={`block w-full focus:outline-none sm:text-sm rounded-md ${
                errors?.payoutsDuration
                  ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 placeholder-gray-400 focus:ring-brandBlue-500 focus:border-brandBlue-500'
              }`}
              {...register('payoutDurationFrequencyUnitUserInput')}
            >
              <option value="months">{tL('parameters.frequencyUnit.1')}</option>
              <option value="years">{tL('parameters.frequencyUnit.12')}</option>
              <option value="indefinite">{tL('parameters.frequencyUnit.0')}</option>
            </select>
          </div>
        </div>
        <p className="mt-2 text-sm text-red-600">{errors?.payoutsDuration?.message}</p>
      </div>

      <Controller
        name="payoutsTaxable"
        control={control}
        render={({ field: { onChange, onBlur, value, ref } }) => (
          <SwitchComponent id="payoutsTaxable" onChange={onChange} onBlur={onBlur} value={value} inputRef={ref} label={tL('payoutsTaxable.label')} tooltip={tL('payoutsTaxable.tooltip')} />
        )}
      />

      <Controller
        name="payoutsIndexed"
        control={control}
        render={({ field: { onChange, onBlur, value, ref } }) => (
          <SwitchComponent id="payoutsIndexed" onChange={onChange} onBlur={onBlur} value={value} inputRef={ref} label={tL('payoutsIndexed.label')} tooltip={tL('payoutsIndexed.tooltip')} />
        )}
      />

      <input type="hidden" {...register('payoutsDuration')} />
    </div>
  );
}

Step4Payouts.propTypes = {
  index: PropTypes.number.isRequired,
  updateWorkflowStatus: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  form: PropTypes.object.isRequired,
  signalNext: PropTypes.bool,
  setSignalNext: PropTypes.func,
};

Step4Payouts.defaultProps = {
  signalNext: false,
  setSignalNext: () => {},
};

export default Step4Payouts;
